export const useWorksheetDetailsStore = defineStore('worksheetDetails', () => {
  const worksheetAPI = useWorksheetAPI();
  const appApiFetch = getAppApiFetch();
  const router = useRouting();
  const { t } = useI18n();
  const { openToast } = useUserMessages();
  const userConfigurationStore = useUserConfigurationStore();

  const worksheetById = ref<WorksheetViewResponseDTO>();
  const projectId = computed(() => worksheetById.value?.ProjectID ?? -1);
  const secondaryStatuses = reactive<WorksheetStatusContainerDTO>({
    SecondaryStatuses: [],
    FitterNote: ''
  });
  const employees = ref<EmployeesOnWorksheetContainerDTO>();
  const projectItemCounts = ref<ProjectItemCounts>();

  let isLoadingWorksheet = false;

  const isWorksheetReadOnly = computed(() => {
    return (worksheetById.value?.StatusEnum === WorksheetStatus.Completed || worksheetById.value?.StatusEnum === WorksheetStatus.Closed) ? true : false;
  });

  async function getWorksheetById(worksheetId: number) {
    if (isLoadingWorksheet) {
      return; 
    }
    
    isLoadingWorksheet = true;
    const getWorksheetByIdResponse = await worksheetAPI.getWorksheetByID(worksheetId);

    if (getWorksheetByIdResponse && !getWorksheetByIdResponse?.HasErrors) {
      worksheetById.value = getWorksheetByIdResponse.Data;
    }

    isLoadingWorksheet = false;
  }

  async function getSecondaryStatuses(worksheetId: number) {
    const getSecondaryStatusesResponse = await worksheetAPI.getSecondaryStatuses(worksheetId);

    if (getSecondaryStatusesResponse && !getSecondaryStatusesResponse?.HasErrors) {
      secondaryStatuses.SecondaryStatuses = getSecondaryStatusesResponse.Data.SecondaryStatuses;
      secondaryStatuses.FitterNote = getSecondaryStatusesResponse.Data.FitterNote;
    }
  }

  async function getEmployeesOnWorksheet(worksheetId: number) {
    const getEmployeesOnWorksheetResponse = await worksheetAPI.getEmployeesOnWorksheet(worksheetId);

    if (getEmployeesOnWorksheetResponse && !getEmployeesOnWorksheetResponse?.HasErrors) {
      employees.value = getEmployeesOnWorksheetResponse.Data;
    }
  }

  async function refreshProjectItemCounts() {
    if (!worksheetById.value) {
      await getWorksheetById(Number(router.route.params.worksheetId));
    }

    const url = 'V3/Worksheet/GetProjectItemCounts?projectId=' + projectId.value + '&worksheet=' + router.route.params.worksheetId + '&fitterId=' + userConfigurationStore.fitterId;

    try {
      const response = await appApiFetch<ResponseDTO<ProjectItemCounts>>(url, { method: 'GET' });

      if (response && response.HasErrors === false) {
        projectItemCounts.value = response.Data;
      } else {
        openToast(t('alert.errorOccurred'), ToastType.ERROR);
      }
    } catch {
      openToast(t('alert.errorOccurred'), ToastType.ERROR);
    }
  }

  interface UpdateStatusParams {
    primaryStatus: WorksheetStatus;
    secondaryStatuses: WorksheetSecondaryStatusDTO[];
    fitterNote: string;
  }

  function updateStatus(status: UpdateStatusParams) {
    if (worksheetById.value) {
      worksheetById.value.StatusEnum = status.primaryStatus;
      secondaryStatuses.SecondaryStatuses = JsonCopy(status.secondaryStatuses);
      secondaryStatuses.FitterNote = status.fitterNote;
    }
  }

  return {
    _worksheetById: worksheetById,
    worksheetById: readonly(worksheetById),
    projectId: readonly(projectId),
    secondaryStatuses: readonly(secondaryStatuses),
    _secondaryStatuses: secondaryStatuses,
    employees: readonly(employees),
    _employees: employees,
    _projectItemCounts: projectItemCounts,
    projectItemCounts: readonly(projectItemCounts),
    isWorksheetReadOnly,
    getWorksheetById,
    getSecondaryStatuses,
    getEmployeesOnWorksheet,
    updateStatus,
    refreshProjectItemCounts
  };
});
